import { useTranslation } from 'react-i18next';
import { Button, Tag } from 'antd';
import resetIcon from '../../../../../../img/reset.svg';

/**
 * Filter label mappings for translation
 * @type {Object<string, string>}
 */
const FILTER_LABELS = {
  // Step filters
  cultures: 'treatment-search|filters.cultures',
  cultural_practices: 'treatment-search|filters.cultural_practices',
  treatment_types: 'treatment-search|filters.treatment_types',
  application_types: 'treatment-search|filters.application_types',
  // Enemies
  enemies: 'treatment-search|filters.enemies',
  // Reseeding
  reseeding_cultures: 'treatment-search|filters.reseeding_cultures',
  reseeding_deadlines: 'treatment-search|filters.reseeding_deadlines',
  // Agronomic filters
  commercial_products: 'treatment-search|filters.commercial_products',
  active_substances: 'treatment-search|filters.active_substances',
  pesticide_groups: 'treatment-search|filters.pesticide_groups',
  intervention_periods: 'treatment-search|filters.intervention_periods',
  culture_stages: 'treatment-search|filters.culture_stages',
  organic_approval: 'treatment-search|filters.organic_approval',
  soil_types: 'treatment-search|filters.soil_types',
  intercrop_cultures: 'treatment-search|filters.intercrop_cultures',
  chemical_families: 'treatment-search|filters.chemical_families',
  pesticide_categories: 'treatment-search|filters.pesticide_categories',
  treatment_name: 'treatment-search|filters.treatment_name',
  application_methods: 'treatment-search|filters.application_methods',
  application_zones: 'treatment-search|filters.application_zones',
  surfaces_to_treat: 'treatment-search|filters.surfaces_to_treat',
  techniques_or_application_places: 'treatment-search|filters.techniques_or_application_places',
  approval_status: 'treatment-search|filters.approval_status',
  approval_in_canada: 'treatment-search|filters.approval_in_canada',
  herbicide_tolerant_variety: 'treatment-search|filters.herbicide_tolerant_variety',
  weed_stages: 'treatment-search|filters.weed_stages',
  // Health risk filters
  acute_toxicity: 'treatment-search|filters.acute_toxicity',
  long_term_effects: 'treatment-search|filters.long_term_effects',
  // Environment risk filters
  fish_or_daphnia: 'treatment-search|filters.aquatic_organisms',
  birds: 'treatment-search|filters.birds',
  bees: 'treatment-search|filters.bees',
  persistence: 'treatment-search|filters.persistence',
  leaching_potential: 'treatment-search|filters.mobility',
};

/**
 * Transforms filters object into active filter display format
 * @param {Object} filters - Current filter state
 * @param {Function} getFilterLabel - Function to get display labels
 * @param {Function} t - Translation function
 * @returns {Array<Object>} Array of active filter objects
 */
const getActiveFilters = (filters, getFilterLabel, t) => {
  return Object.entries(filters || {})
    .filter(([key, value]) => {
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        key !== 'page' &&
        key !== 'size'
      );
    })
    .map(([key, value]) => ({
      key,
      label: FILTER_LABELS[key] ? t(FILTER_LABELS[key]) : key,
      values: value.map((v) => ({
        id: v,
        label: getFilterLabel(key, v),
      })).filter((item) => item.label),
    }))
    .filter((filter) => filter.values.length > 0);
};



/**
 * Active filters display section
 * @param {Object} props - Component props
 * @param {Array} props.activeFilters - List of active filters
 * @param {Function} props.onResetFilters - Reset filters callback
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Active filters section
 */
const ActiveFiltersSection = ({ activeFilters, onResetFilters, onRemoveFilter, t, hasFilters }) => {
  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const filterCount = validFilters.reduce((sum, filter) => sum + filter.values.length, 0);

  return (
    <div className="bg-green-900 text-lg p-6 rounded-2xl mb-8">
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-medium m-0 normal-case">
          {t('common|Active filters')} {filterCount > 0 && `(${filterCount})`}
        </h3>
        <div className="flex gap-2">
          <Button size="large" onClick={onResetFilters} className="reset-button-with-icon">
            <img src={resetIcon} alt="Reset" className="w-4 h-4 reset-icon" />
            {t('common|Reset')}
          </Button>
        </div>
      </div>
      {validFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {validFilters.map((filter) =>
            filter.values.map((value) => (
              <Tag
                key={`${filter.key}-${value.id}`}
                closable
                onClose={() => onRemoveFilter(filter.key, value.id)}
              >
                <strong>{filter.label}:</strong> {value.label}
              </Tag>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main recap component that displays active filters with options to reset or share results
 * Works with nuqs for automatic URL synchronization
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter state
 * @param {Function} props.onResetFilters - Callback to reset all filters
 * @param {Function} props.getFilterLabel - Function to get display label for filter values
 * @returns {JSX.Element} Recap component with filters and legend
 */
const Recap = ({ filters, onResetFilters, onRemoveFilter, getFilterLabel }) => {
  const { t } = useTranslation();

  const activeFilters = getActiveFilters(filters, getFilterLabel, t);

  const hasAnyFilters = Object.entries(filters || {}).some(
    ([key, value]) =>
      Array.isArray(value) &&
      value.length > 0 &&
      key !== 'page' &&
      key !== 'size'
  );


  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const hasValidLabels = validFilters.length > 0;

  return (
    <>
      {hasValidLabels && (
        <ActiveFiltersSection
          activeFilters={activeFilters}
          onResetFilters={onResetFilters}
          onRemoveFilter={onRemoveFilter}
          hasFilters={hasAnyFilters}
          t={t}
        />
      )}
    </>
  );
};

export default Recap;
